import Phaser from 'phaser';

// TODO: replace with real DJI-Mini-style spritesheet in Sprint 4.
// Reference photos live in _input/drone-reference/. No DJI marks on the body.
function makeDroneTexture(scene, key) {
  const size = 32;
  const cx = size / 2;
  const cy = size / 2;

  const bodyWhite = 0xeaeaea;
  const bodyShadow = 0xb8b8b8;
  const armGray = 0x3a3a3a;
  const rotorGray = 0x2a2a2a;
  const rotorBladeGray = 0x6a6a6a;
  const accentYellow = 0xf6b93b;
  const cameraDark = 0x1a1a1a;

  const g = scene.add.graphics({ x: 0, y: 0 });

  // Arms — X-frame extending from body to rotor centers
  const rotorPositions = [
    [6, 6],   // front-left
    [26, 6],  // front-right
    [6, 26],  // rear-left
    [26, 26], // rear-right
  ];
  g.lineStyle(3, armGray, 1);
  for (const [rx, ry] of rotorPositions) {
    g.lineBetween(cx, cy, rx, ry);
  }

  // Rotor blade discs (lighter, simulates motion blur)
  g.fillStyle(rotorBladeGray, 0.45);
  for (const [rx, ry] of rotorPositions) {
    g.fillCircle(rx, ry, 5);
  }

  // Rotor hubs (dark center)
  g.fillStyle(rotorGray, 1);
  for (const [rx, ry] of rotorPositions) {
    g.fillCircle(rx, ry, 2.5);
  }

  // Yellow propeller-tip accents — two opposing dots per rotor
  g.fillStyle(accentYellow, 1);
  for (const [rx, ry] of rotorPositions) {
    g.fillCircle(rx - 4, ry, 1.5);
    g.fillCircle(rx + 4, ry, 1.5);
  }

  // Body — rounded oblong, long axis = forward (north)
  // Shadow under body for slight depth
  g.fillStyle(bodyShadow, 0.5);
  g.fillRoundedRect(cx - 5, cy - 7 + 1, 10, 14, 4);
  // Body fill
  g.fillStyle(bodyWhite, 1);
  g.fillRoundedRect(cx - 5, cy - 7, 10, 14, 4);
  // Body outline
  g.lineStyle(1, 0x000000, 0.4);
  g.strokeRoundedRect(cx - 5, cy - 7, 10, 14, 4);

  // Camera gimbal (front)
  g.fillStyle(cameraDark, 1);
  g.fillCircle(cx, cy - 5, 1.8);
  g.fillStyle(0x4a90c2, 0.9);
  g.fillCircle(cx, cy - 5, 0.9);

  // Battery indicator (rear) — 4 tiny lights
  g.fillStyle(accentYellow, 0.9);
  for (let i = 0; i < 4; i++) {
    g.fillRect(cx - 3 + i * 2, cy + 4, 1, 1);
  }

  g.generateTexture(key, size, size);
  g.destroy();
}

export class BootScene extends Phaser.Scene {
  constructor() {
    super('BootScene');
  }

  preload() {
    // Sprout Lands sheets — sliced into individual textureKeys in create().
    // Loader silently swallows 404s so missing files fall back to programmatic.
    this.load.on('loaderror', (file) => {
      if (file.key?.startsWith('iw-src:')) {
        // Expected for missing optional assets — programmatic fallback handles it
      }
    });
    // NOTE: Sprout Lands' "character" sheets are chibi BUNNIES, not humans.
    // Pixel-Ian (a human) stays programmatic until Round 3 generates a human in style.
    // this.load.image('iw-src:npc-pixel-ian-sheet', 'assets/sprites/npc-pixel-ian-sheet.png');
    this.load.image('iw-src:signs-sheet', 'assets/sprites/signs-sheet.png');
    this.load.image('iw-src:mailbox-sheet', 'assets/sprites/mailbox-sheet.png');
    this.load.image('iw-src:fences-sheet', 'assets/sprites/fences-sheet.png');
    this.load.image('iw-src:furniture-sheet', 'assets/sprites/furniture-sheet.png');

    // Tilesets (used by HouseScene directly — no slicing needed)
    this.load.image('tileset-grass', 'assets/tilesets/grass-tiles.png');
    this.load.image('tileset-stone', 'assets/tilesets/stone-tiles.png');
    this.load.image('tileset-wood-walls', 'assets/tilesets/wood-walls.png');

    // Phone app icons — Remi loads as a real PNG; the others are programmatic.
    this.load.image('app-remi', 'assets/sprites/app-remi.png');

    // ─── v1 Studio AI overlay sprites (transparent PNGs) ────────────────
    // Each is loaded if present; if the file is missing, the loader 404s
    // silently and the programmatic placeholder in create() registers
    // under the same texture key (the create() guards `if (!exists)`).
    // To add a new AI overlay: render → save here → reload → done.
    this.load.image('gr-eames-chair', 'assets/sprites/eames-chair.png');
    this.load.image('studio-worktable', 'assets/sprites/worktable.png');
    this.load.image('studio-bookshelf', 'assets/sprites/bookshelf.png');
    this.load.image('studio-tv', 'assets/sprites/tv.png');
    this.load.image('studio-arcade-cabinet', 'assets/sprites/arcade-cabinet.png');
    this.load.image('studio-mailbox', 'assets/sprites/mailbox-studio.png');

    // ─── Room background scenes (Path D: AI-rendered backgrounds) ───────
    // Each room can specify a `backgroundImage` in world.config.js. If the
    // file exists, the room renders as the AI image with sprite overlays
    // on top; if not, the room falls back to programmatic sprite rendering.
    // 404s are silently swallowed so the fallback works during development
    // before all room images are authored.
    this.load.image('scene-studio', 'assets/scenes/studio.png');
    // v2+ revival: also load the great-room shell when that build comes back.
    this.load.image('scene-great-room', 'assets/scenes/great-room.png');
  }

  create() {
    // For each migrated key, slice a frame from a Sprout Lands sheet and register
    // it as a standalone texture. Falls through to programmatic if the source isn't loaded.
    // sliceFromSheet(this, 'iw-src:npc-pixel-ian-sheet', 'npc-pixel-ian', 0, 0, 48, 48);
    // ↑ disabled — Sprout Lands character is a chibi bunny, not a human. Stays programmatic.
    sliceFromSheet(this, 'iw-src:signs-sheet', 'object-currently-board', 0, 0, 16, 16);
    sliceFromSheet(this, 'iw-src:mailbox-sheet', 'mailbox', 0, 0, 16, 32);
    sliceFromSheet(this, 'iw-src:fences-sheet', 'gate', 16, 16, 16, 32);
    sliceFromSheet(this, 'iw-src:furniture-sheet', 'bookshelf', 0, 0, 16, 32);

    // Slice fill tiles from tilesets (for TileSprite-based room rendering)
    // Standard Sprout Lands autotile layout: position (1,1) of the top-left 4×4 block is the interior fill
    sliceFromSheet(this, 'tileset-grass', 'tile-grass-fill', 16, 16, 16, 16);
    sliceFromSheet(this, 'tileset-stone', 'tile-stone-fill', 16, 16, 16, 16);
    sliceFromSheet(this, 'tileset-wood-walls', 'tile-wood-wall', 16, 0, 16, 16);

    // Programmatic fallbacks — only run if a real PNG didn't already register the key
    if (!this.textures.exists('drone-default')) makeDroneTexture(this, 'drone-default');
    if (!this.textures.exists('npc-pixel-ian')) makeNpcTexture(this, 'npc-pixel-ian');
    if (!this.textures.exists('object-currently-board')) makeBoardTexture(this, 'object-currently-board');

    // Workshop workbenches — no pack equivalent, programmatic with palette colors
    makeWorkbenchTexture(this, 'workbench-remi', 0xff8fa3, 'heart');
    makeWorkbenchTexture(this, 'workbench-bracket', 0xffb84a, 'bracket');
    makeWorkbenchTexture(this, 'workbench-apartment', 0x7ad7d4, 'pin');
    makeWorkbenchTexture(this, 'workbench-dofh', 0xf6e27f, 'trophy');

    // Living room — bookshelf may be sliced; tv/chair stay programmatic for now
    if (!this.textures.exists('bookshelf')) makeBookshelfTexture(this, 'bookshelf');
    makeTvTexture(this, 'tv');
    makeChairTexture(this, 'chair-layne');

    // Exterior — mailbox + gate may be sliced; cat/appa stay programmatic
    if (!this.textures.exists('mailbox')) makeMailboxTexture(this, 'mailbox');
    if (!this.textures.exists('gate')) makeGateTexture(this, 'gate');
    makeCatTexture(this, 'npc-cat');
    makeAppaTexture(this, 'npc-appa');

    // Phone app icons — pixel-rendered, 32×32 source
    makeCaffeineRushIcon(this, 'app-caffeine-rush');
    makeIanWorldIcon(this, 'app-ian-world');
    // app-remi loaded as real PNG above
    makeLinkedinIcon(this, 'app-linkedin');
    makeGithubIcon(this, 'app-github');
    makeInstagramIcon(this, 'app-instagram');
    makeSpotifyIcon(this, 'app-spotify');

    // ─── Hero objects (great-room + pool deck) ──────────────────────────
    // Authored at 32-96 px native. At 16-tier, they display at native size
    // (scale 1) and span 2-3 tiles each. Composition targets the locked
    // Gemini great-room reference — see ROUND-3-AUDITION-INTERIORS.md
    // "Locked great-room reference."
    makeKitchenIslandTexture(this, 'gr-kitchen-island');
    if (!this.textures.exists('gr-eames-chair')) makeEamesChairTexture(this, 'gr-eames-chair');
    makeFireplaceTexture(this, 'gr-fireplace');
    makeDiningTableTexture(this, 'gr-dining-table');
    makeSectionalSofaTexture(this, 'gr-sectional-sofa');
    makeKitchenBackWallTexture(this, 'gr-kitchen-back');
    makeKundigPosterTexture(this, 'gr-kundig-poster');
    makeRugTexture(this, 'gr-rug');
    makeFloorLampTexture(this, 'gr-floor-lamp');
    makeSnakePlantTexture(this, 'gr-snake-plant');
    makeLoungerTexture(this, 'pd-lounger');

    // ─── v1 Studio objects (programmatic placeholders) ──────────────────
    // Each `if (!exists)` guard: AI PNG (loaded via this.load.image in
    // preload()) wins if present; programmatic version is the fallback.
    // To upgrade a sprite: render the AI version → save it as the file
    // referenced in preload() → reload. The programmatic version stays
    // in the codebase as the safety net.
    makeStudioRemiSketchTexture(this, 'studio-remi-sketch');
    makeStudioBracketPrintTexture(this, 'studio-bracket-print');
    makeStudioAptHunterMapTexture(this, 'studio-apthunter-map');
    makeStudioDofhTrophyTexture(this, 'studio-dofh-trophy');
    if (!this.textures.exists('studio-worktable')) makeStudioWorktableTexture(this, 'studio-worktable');
    if (!this.textures.exists('studio-tv')) makeStudioTvTexture(this, 'studio-tv');
    if (!this.textures.exists('studio-arcade-cabinet')) makeStudioArcadeCabinetTexture(this, 'studio-arcade-cabinet');
    makeStudioNeighborPhotoTexture(this, 'studio-neighbor-photo');
    if (!this.textures.exists('studio-bookshelf')) makeStudioBookshelfTexture(this, 'studio-bookshelf');
    makeStudioCurrentlyBoardTexture(this, 'studio-currently-board');
    if (!this.textures.exists('studio-mailbox')) makeStudioMailboxTexture(this, 'studio-mailbox');
    makeWorktableLaptopTexture(this, 'studio-worktable-laptop');
    makeStudioYogaMatTexture(this, 'studio-yoga-mat');
    makeStudioBallBasketTexture(this, 'studio-ball-basket');

    // Mobile: textures are registered, hand off to MobileShell (no further scenes).
    if (window.__iwMobile__) {
      this.game.events.emit('iw-boot-complete');
      return;
    }

    // Route by URL hash: #lab → asset preview, #audition → style picker, else cinematic intro
    const hash = window.location.hash;
    let next = 'IntroScene';
    if (hash === '#lab') next = 'LabScene';
    else if (hash === '#audition') next = 'StyleAuditionScene';
    this.scene.start(next);
  }
}

// Slice a sub-rectangle from a loaded source image into a new standalone texture.
// Uses Phaser's CanvasTexture API; after slicing, the new key is usable like any sprite.
function sliceFromSheet(scene, sourceKey, destKey, sx, sy, w, h) {
  if (!scene.textures.exists(sourceKey)) return false;
  if (scene.textures.exists(destKey)) return true;
  const sourceImage = scene.textures.get(sourceKey).getSourceImage();
  const canvas = scene.textures.createCanvas(destKey, w, h);
  canvas.context.drawImage(sourceImage, sx, sy, w, h, 0, 0, w, h);
  canvas.refresh();
  return true;
}

// Catalog of textureKeys grouped by section, for LabScene to iterate.
// When real PNGs land in /public/assets/sprites/<key>.png, BootScene will load them
// in addition to the programmatic versions, and the lab will show side-by-side.
export const SPRITE_CATALOG = [
  {
    section: 'drone',
    items: [
      { key: 'drone-default', label: 'Drone (default)', logicalSize: '32×32' },
    ],
  },
  {
    section: 'characters',
    items: [
      { key: 'npc-pixel-ian', label: 'Pixel Ian', logicalSize: '18×26' },
      { key: 'npc-cat', label: 'Sidewalk cat', logicalSize: '14×12' },
      { key: 'npc-appa', label: 'Appa', logicalSize: '22×16' },
    ],
  },
  {
    section: 'objects · interactive',
    items: [
      { key: 'object-currently-board', label: 'Currently board', logicalSize: '16×16' },
      { key: 'mailbox', label: 'Mailbox', logicalSize: '14×18' },
      { key: 'gate', label: 'Gate', logicalSize: '16×20' },
    ],
  },
  {
    section: 'workshop · workbenches',
    items: [
      { key: 'workbench-remi', label: 'Remi', logicalSize: '16×16' },
      { key: 'workbench-bracket', label: 'Perfect Bracket', logicalSize: '16×16' },
      { key: 'workbench-apartment', label: 'Apartment Hunter', logicalSize: '16×16' },
      { key: 'workbench-dofh', label: 'DOFH Fantasy Site', logicalSize: '16×16' },
    ],
  },
  {
    section: 'living room',
    items: [
      { key: 'bookshelf', label: 'Bookshelf', logicalSize: '18×24' },
      { key: 'tv', label: 'TV', logicalSize: '20×16' },
      { key: 'chair-layne', label: "Layne's chair", logicalSize: '16×18' },
    ],
  },
  {
    section: 'studio · v1',
    items: [
      { key: 'studio-worktable', label: 'Worktable', logicalSize: '30×14' },
      { key: 'studio-bookshelf', label: 'Studio bookshelf', logicalSize: '18×30' },
      { key: 'studio-currently-board', label: 'Currently board (cork)', logicalSize: '24×16' },
      { key: 'studio-tv', label: 'TV (mounted)', logicalSize: '28×18' },
      { key: 'studio-arcade-cabinet', label: 'Caffeine Rush arcade', logicalSize: '18×30' },
      { key: 'studio-neighbor-photo', label: 'Neighbor photo', logicalSize: '20×16' },
    ],
  },
  {
    section: 'studio · projects (diegetic)',
    items: [
      { key: 'studio-remi-sketch', label: 'Remi heart sketch', logicalSize: '18×18' },
      { key: 'studio-bracket-print', label: 'Perfect Bracket print', logicalSize: '24×18' },
      { key: 'studio-apthunter-map', label: 'Apartment Hunter map', logicalSize: '20×18' },
      { key: 'studio-dofh-trophy', label: 'DOFH trophy', logicalSize: '14×18' },
      { key: 'studio-worktable-laptop', label: 'Worktable laptop', logicalSize: '16×12' },
    ],
  },
];

// Pixel-Ian: down-facing 18×26 chunky-pixel character. Olive henley, dark
// charcoal pants, tousled brown hair. Reads as "outdoorsy guy in his
// studio" rather than the more generic rust-shirt v1.
function makeNpcTexture(scene, key) {
  const w = 18;
  const h = 26;
  const g = scene.add.graphics({ x: 0, y: 0 });

  // Soft ground shadow
  g.fillStyle(0x000000, 0.28);
  g.fillEllipse(w / 2, h - 1, 12, 3);

  // Legs (charcoal pants, slight lighter inseam highlight)
  const PANTS = 0x2a2e34;
  const PANTS_HI = 0x3a3f47;
  g.fillStyle(PANTS, 1);
  g.fillRect(5, 18, 3, 6);
  g.fillRect(10, 18, 3, 6);
  g.fillStyle(PANTS_HI, 1);
  g.fillRect(5, 18, 1, 4);
  g.fillRect(10, 18, 1, 4);
  // Boots
  g.fillStyle(0x1a1614, 1);
  g.fillRect(5, 23, 3, 1);
  g.fillRect(10, 23, 3, 1);

  // Torso — olive henley with two-button placket
  const SHIRT = 0x67835C;
  const SHIRT_HI = 0x7E9A70;
  const SHIRT_SH = 0x4F6645;
  g.fillStyle(SHIRT, 1);
  g.fillRect(4, 11, 10, 8);
  // Left side highlight
  g.fillStyle(SHIRT_HI, 0.7);
  g.fillRect(4, 11, 1, 7);
  // Right side shadow
  g.fillStyle(SHIRT_SH, 0.7);
  g.fillRect(13, 11, 1, 7);
  // Henley placket (vertical line center, two button dots)
  g.fillStyle(SHIRT_SH, 1);
  g.fillRect(8, 12, 1, 4);
  g.fillStyle(0xC9A55A, 1);
  g.fillRect(8, 12, 1, 1);
  g.fillRect(8, 14, 1, 1);
  // Collar hint
  g.fillStyle(SHIRT_SH, 1);
  g.fillRect(7, 11, 4, 1);

  // Arms (sleeves)
  g.fillStyle(SHIRT, 1);
  g.fillRect(2, 12, 2, 6);
  g.fillRect(14, 12, 2, 6);
  // Sleeve cuffs (slight dark band)
  g.fillStyle(SHIRT_SH, 0.7);
  g.fillRect(2, 17, 2, 1);
  g.fillRect(14, 17, 2, 1);
  // Hands
  const SKIN = 0xe7c39c;
  const SKIN_SH = 0xc9a378;
  g.fillStyle(SKIN, 1);
  g.fillRect(2, 18, 2, 2);
  g.fillRect(14, 18, 2, 2);

  // Neck
  g.fillStyle(SKIN_SH, 1);
  g.fillRect(7, 10, 4, 1);

  // Head
  g.fillStyle(SKIN, 1);
  g.fillRect(5, 3, 8, 8);
  // Face shadow on right side
  g.fillStyle(SKIN_SH, 0.6);
  g.fillRect(12, 4, 1, 6);

  // Hair — tousled black, slight cowlick on left
  const HAIR = 0x1a1614;
  const HAIR_HI = 0x2e2826;
  g.fillStyle(HAIR, 1);
  g.fillRect(4, 2, 10, 3);
  g.fillRect(4, 5, 1, 3);
  g.fillRect(13, 5, 1, 3);
  // Cowlick / part hint
  g.fillStyle(HAIR_HI, 1);
  g.fillRect(5, 2, 2, 1);
  g.fillRect(8, 2, 2, 1);

  // Eyebrows (subtle)
  g.fillStyle(HAIR, 1);
  g.fillRect(6, 6, 2, 1);
  g.fillRect(10, 6, 2, 1);

  // Eyes
  g.fillStyle(0x14110e, 1);
  g.fillRect(6, 7, 1, 1);
  g.fillRect(11, 7, 1, 1);

  // Tiny smile (1px)
  g.fillStyle(0x6b4523, 0.7);
  g.fillRect(8, 9, 2, 1);

  g.generateTexture(key, w, h);
  g.destroy();
}

// Workshop workbench: dark base with a glowing screen + project icon
function makeWorkbenchTexture(scene, key, accent, icon) {
  const w = 16;
  const h = 16;
  const g = scene.add.graphics({ x: 0, y: 0 });
  // Bench top
  g.fillStyle(0x1a1530, 1);
  g.fillRect(0, 6, w, 10);
  // Bench legs
  g.fillStyle(0x0a0716, 1);
  g.fillRect(1, 14, 2, 2);
  g.fillRect(13, 14, 2, 2);
  // Glowing screen panel
  g.fillStyle(accent, 0.3);
  g.fillRect(2, 1, w - 4, 6);
  g.fillStyle(accent, 0.95);
  g.fillRect(3, 2, w - 6, 4);
  // Icon overlay
  g.fillStyle(0x0a0716, 1);
  if (icon === 'heart') {
    // simple pixel heart
    g.fillRect(6, 3, 1, 1); g.fillRect(9, 3, 1, 1);
    g.fillRect(5, 4, 6, 1);
    g.fillRect(6, 5, 4, 1);
    g.fillRect(7, 6, 2, 1);
  } else if (icon === 'bracket') {
    // bracket lines like a tournament tree
    g.fillRect(5, 3, 1, 3);
    g.fillRect(10, 3, 1, 3);
    g.fillRect(5, 3, 6, 1);
    g.fillRect(7, 4, 2, 1);
    g.fillRect(8, 5, 1, 1);
  } else if (icon === 'pin') {
    // map pin
    g.fillRect(7, 2, 2, 1);
    g.fillRect(6, 3, 4, 2);
    g.fillRect(7, 5, 2, 1);
  } else if (icon === 'trophy') {
    g.fillRect(6, 2, 4, 1);
    g.fillRect(6, 3, 4, 2);
    g.fillRect(7, 5, 2, 1);
    g.fillRect(7, 6, 2, 0.5);
  }
  g.generateTexture(key, w, h);
  g.destroy();
}

// Bookshelf: tall (24px) — multiple stacked spines with subtle color variation
function makeBookshelfTexture(scene, key) {
  const w = 18;
  const h = 24;
  const g = scene.add.graphics({ x: 0, y: 0 });
  // Frame
  g.fillStyle(0x4a2f1c, 1);
  g.fillRect(0, 0, w, h);
  // Inner shelf area
  g.fillStyle(0x2c1a0e, 1);
  g.fillRect(2, 2, w - 4, h - 4);

  const shelves = 3;
  const shelfH = (h - 4) / shelves;
  const spineColors = [0x8b3a3a, 0xc8965a, 0x3a5f8b, 0x6f8b3a, 0x8b6b3a, 0xb88c54];
  for (let s = 0; s < shelves; s++) {
    const yTop = 2 + s * shelfH;
    let x = 3;
    let i = 0;
    while (x < w - 3) {
      const sw = 1 + (i % 3);
      g.fillStyle(spineColors[(s * 3 + i) % spineColors.length], 1);
      g.fillRect(x, yTop + 1, sw, shelfH - 2);
      x += sw + 1;
      i += 1;
    }
    // Shelf board
    g.fillStyle(0x4a2f1c, 1);
    g.fillRect(2, 2 + (s + 1) * shelfH - 1, w - 4, 1);
  }
  g.generateTexture(key, w, h);
  g.destroy();
}

// TV: dark cabinet with a glowing screen showing tiny "Stardew" green field
function makeTvTexture(scene, key) {
  const w = 20;
  const h = 16;
  const g = scene.add.graphics({ x: 0, y: 0 });
  // Stand
  g.fillStyle(0x2c1a0e, 1);
  g.fillRect(7, 13, 6, 2);
  g.fillRect(4, 14, 12, 2);
  // Cabinet
  g.fillStyle(0x0e0e10, 1);
  g.fillRect(0, 0, w, 13);
  // Screen bezel
  g.fillStyle(0x222227, 1);
  g.fillRect(2, 2, w - 4, 9);
  // Screen — tiny pixel-within-pixel of a green field with a brown patch (a nod, not a copy)
  g.fillStyle(0x6f8b3a, 1);
  g.fillRect(3, 3, w - 6, 7);
  g.fillStyle(0x8b6b3a, 1);
  g.fillRect(7, 6, 4, 2);
  g.fillStyle(0xfff2a8, 1);
  g.fillRect(8, 5, 1, 1);
  // Power LED
  g.fillStyle(0xff5050, 1);
  g.fillRect(w - 3, 11, 1, 1);
  g.generateTexture(key, w, h);
  g.destroy();
}

// Chair (Layne's): a small armchair seen from above with a folded blanket
function makeChairTexture(scene, key) {
  const w = 16;
  const h = 18;
  const g = scene.add.graphics({ x: 0, y: 0 });
  // Shadow
  g.fillStyle(0x000000, 0.25);
  g.fillEllipse(w / 2, h - 1, 12, 3);
  // Backrest
  g.fillStyle(0x6b3a3a, 1);
  g.fillRect(2, 0, w - 4, 5);
  // Arms
  g.fillStyle(0x4a2828, 1);
  g.fillRect(0, 3, 3, 12);
  g.fillRect(w - 3, 3, 3, 12);
  // Seat
  g.fillStyle(0x8b4a4a, 1);
  g.fillRect(3, 5, w - 6, 10);
  // Folded blanket
  g.fillStyle(0xf0d8a8, 1);
  g.fillRect(4, 7, w - 8, 4);
  g.fillStyle(0xc8965a, 1);
  g.fillRect(4, 9, w - 8, 1);
  g.generateTexture(key, w, h);
  g.destroy();
}

// "Currently" board: small dark frame with a yellow dot indicator
function makeBoardTexture(scene, key) {
  const size = 16;
  const g = scene.add.graphics({ x: 0, y: 0 });
  // Frame
  g.fillStyle(0x2a2018, 1);
  g.fillRect(0, 0, size, size);
  // Inner panel
  g.fillStyle(0xf4ead5, 1);
  g.fillRect(2, 2, size - 4, size - 4);
  // Pinned note lines
  g.fillStyle(0x6b4f3a, 1);
  g.fillRect(4, 5, 8, 1);
  g.fillRect(4, 8, 8, 1);
  g.fillRect(4, 11, 5, 1);
  // Status indicator dot
  g.fillStyle(0xf6b93b, 1);
  g.fillCircle(size - 3, 3, 1.5);
  g.generateTexture(key, size, size);
  g.destroy();
}

// Mailbox: post + flag (Stardew-ish 3/4 view)
function makeMailboxTexture(scene, key) {
  const w = 14;
  const h = 18;
  const g = scene.add.graphics({ x: 0, y: 0 });
  g.fillStyle(0x000000, 0.25);
  g.fillEllipse(w / 2, h - 1, 8, 2);
  // Post
  g.fillStyle(0x6b4f3a, 1);
  g.fillRect(6, 8, 2, 9);
  // Box body
  g.fillStyle(0x4a6b8c, 1);
  g.fillRect(2, 3, 10, 6);
  // Box top highlight
  g.fillStyle(0x6b8baa, 1);
  g.fillRect(2, 3, 10, 1);
  // Door slot
  g.fillStyle(0x1a2638, 1);
  g.fillRect(3, 5, 6, 3);
  // Red flag
  g.fillStyle(0xc94c4c, 1);
  g.fillRect(12, 4, 2, 2);
  g.generateTexture(key, w, h);
  g.destroy();
}

// Garden gate (post + slatted gate)
function makeGateTexture(scene, key) {
  const w = 16;
  const h = 20;
  const g = scene.add.graphics({ x: 0, y: 0 });
  g.fillStyle(0x000000, 0.25);
  g.fillEllipse(w / 2, h - 1, 12, 2);
  // Posts
  g.fillStyle(0x4a2f1c, 1);
  g.fillRect(0, 2, 2, h - 4);
  g.fillRect(w - 2, 2, 2, h - 4);
  // Slats
  g.fillStyle(0x8b6b3a, 1);
  for (let x = 3; x < w - 3; x += 2) {
    g.fillRect(x, 6, 1, h - 10);
  }
  // Crossbeams
  g.fillRect(2, 6, w - 4, 1);
  g.fillRect(2, h - 6, w - 4, 1);
  // Latch
  g.fillStyle(0xcccccc, 1);
  g.fillRect(w - 4, 11, 1, 2);
  g.generateTexture(key, w, h);
  g.destroy();
}

// Cat sprite — small orange tabby viewed from 3/4
function makeCatTexture(scene, key) {
  const w = 14;
  const h = 12;
  const g = scene.add.graphics({ x: 0, y: 0 });
  g.fillStyle(0x000000, 0.25);
  g.fillEllipse(w / 2, h - 1, 9, 2);
  // Body
  g.fillStyle(0xd97a3a, 1);
  g.fillEllipse(w / 2, 7, 10, 5);
  // Tail (curled up)
  g.fillRect(w - 3, 4, 1, 4);
  g.fillRect(w - 4, 3, 2, 1);
  // Head
  g.fillStyle(0xd97a3a, 1);
  g.fillCircle(4, 6, 3);
  // Ears
  g.fillTriangle(2, 4, 3, 3, 4, 5);
  g.fillTriangle(5, 5, 6, 3, 7, 4);
  // Tabby stripes
  g.fillStyle(0x8c4a1e, 1);
  g.fillRect(7, 6, 1, 3);
  g.fillRect(9, 6, 1, 3);
  // Eyes
  g.fillStyle(0x000000, 1);
  g.fillRect(3, 6, 1, 1);
  g.fillRect(5, 6, 1, 1);
  g.generateTexture(key, w, h);
  g.destroy();
}

// Caffeine Rush app icon — standalone coffee cup with steam wisps on a transparent
// background. Tile color is set by the host (PhoneSystem icon wrap), so the icon itself
// must be transparent everywhere except the cup + steam.
function makeCaffeineRushIcon(scene, key) {
  const size = 32;
  const g = scene.add.graphics({ x: 0, y: 0 });

  // Steam wisps — 3 curls in cream so they read on the purple tile
  g.fillStyle(0xF3E5C2, 0.85);
  // Left wisp
  g.fillRect(8, 3, 1, 2);
  g.fillRect(9, 5, 1, 2);
  g.fillRect(8, 7, 1, 1);
  // Center wisp
  g.fillRect(15, 2, 2, 2);
  g.fillRect(16, 4, 1, 2);
  g.fillRect(15, 6, 2, 1);
  // Right wisp
  g.fillRect(22, 3, 1, 2);
  g.fillRect(23, 5, 1, 2);
  g.fillRect(22, 7, 1, 1);

  // Saucer (back rim — slightly behind cup body)
  g.fillStyle(0x3E2418, 1);
  g.fillRect(4, 26, 24, 1);
  g.fillStyle(0x754C60, 1);
  g.fillRect(3, 27, 26, 2);
  g.fillStyle(0x4A2F3A, 1);
  g.fillRect(3, 28, 26, 1);

  // Cup handle (right side, ear shape)
  g.fillStyle(0xD85A30, 1);
  g.fillRect(23, 14, 4, 2);
  g.fillRect(25, 16, 2, 4);
  g.fillRect(23, 20, 4, 2);
  // Handle outer shadow
  g.fillStyle(0xA13E1A, 1);
  g.fillRect(26, 16, 1, 4);
  g.fillRect(23, 21, 4, 1);

  // Cup body (caffeine red-orange) — centered, fills more of the tile
  g.fillStyle(0xD85A30, 1);
  g.fillRect(6, 11, 18, 15);
  // Cup highlight (top-left light source)
  g.fillStyle(0xE87A4A, 1);
  g.fillRect(6, 11, 18, 1);
  g.fillRect(6, 11, 1, 15);
  // Cup shadow (bottom-right)
  g.fillStyle(0xA13E1A, 1);
  g.fillRect(23, 12, 1, 14);
  g.fillRect(7, 25, 16, 1);

  // Coffee surface (dark brown disc with rim highlight)
  g.fillStyle(0x3E2418, 1);
  g.fillRect(7, 11, 16, 3);
  g.fillStyle(0x6B3E26, 1);
  g.fillRect(7, 11, 16, 1);
  g.fillStyle(0x2A180F, 1);
  g.fillRect(7, 13, 16, 1);

  g.generateTexture(key, size, size);
  g.destroy();
}

// Ian's World app icon — standalone pixel house on a transparent background.
// The recursive joke: tap it, the phone screen tells you you're already inside it.
function makeIanWorldIcon(scene, key) {
  const size = 32;
  const g = scene.add.graphics({ x: 0, y: 0 });

  // House walls (warm wood) — bigger now that we removed the inner frame
  g.fillStyle(0xC49A6C, 1);
  g.fillRect(5, 16, 22, 13);
  // Wall shadow (right side)
  g.fillStyle(0xAA7959, 1);
  g.fillRect(25, 16, 2, 13);
  // Wall highlight (left)
  g.fillStyle(0xDCB98A, 1);
  g.fillRect(5, 16, 1, 13);
  // Wall foundation shadow
  g.fillStyle(0x754C60, 1);
  g.fillRect(5, 28, 22, 1);

  // Roof — chunky triangle stepping up to a peak
  g.fillStyle(0x8B3A3A, 1);
  g.fillRect(3, 14, 26, 2);
  g.fillRect(5, 12, 22, 2);
  g.fillRect(7, 10, 18, 2);
  g.fillRect(9, 8, 14, 2);
  g.fillRect(11, 6, 10, 2);
  g.fillRect(13, 4, 6, 2);
  g.fillRect(15, 3, 2, 1);
  // Roof shadow (right side)
  g.fillStyle(0x6B2828, 1);
  g.fillRect(27, 14, 2, 2);
  g.fillRect(25, 12, 2, 2);
  g.fillRect(23, 10, 2, 2);
  g.fillRect(21, 8, 2, 2);
  // Roof highlight (left edge — top-left light source)
  g.fillStyle(0xA85050, 1);
  g.fillRect(3, 14, 2, 1);

  // Door (centered, taller)
  g.fillStyle(0x4A2F1C, 1);
  g.fillRect(14, 20, 4, 9);
  g.fillStyle(0x2A180F, 1);
  g.fillRect(17, 20, 1, 9);
  // Doorknob
  g.fillStyle(0xF6E27F, 1);
  g.fillRect(16, 24, 1, 1);

  // Windows — lit warm yellow squares with cross frames
  g.fillStyle(0xF6E27F, 1);
  g.fillRect(8, 19, 4, 4);
  g.fillRect(20, 19, 4, 4);
  // Window cross frames
  g.fillStyle(0x754C60, 1);
  g.fillRect(10, 19, 1, 4);
  g.fillRect(8, 21, 4, 1);
  g.fillRect(22, 19, 1, 4);
  g.fillRect(20, 21, 4, 1);

  // Tiny chimney puff (top of roof, left)
  g.fillStyle(0xC1C8B9, 0.7);
  g.fillRect(8, 2, 1, 1);
  g.fillRect(9, 1, 2, 1);

  g.generateTexture(key, size, size);
  g.destroy();
}

// Appa — fluffy white sky-bison-shaped lump (the bare minimum to read as Appa from a distance)
function makeAppaTexture(scene, key) {
  const w = 22;
  const h = 16;
  const g = scene.add.graphics({ x: 0, y: 0 });
  g.fillStyle(0x000000, 0.25);
  g.fillEllipse(w / 2, h - 1, 16, 3);
  // Big fluffy body
  g.fillStyle(0xf2ead2, 1);
  g.fillEllipse(w / 2, h / 2 + 1, 18, 10);
  // Six leg dots peeking out
  g.fillStyle(0xd8cfa8, 1);
  g.fillRect(3, h - 4, 2, 2);
  g.fillRect(w - 5, h - 4, 2, 2);
  g.fillRect(8, h - 3, 2, 2);
  g.fillRect(w - 10, h - 3, 2, 2);
  // Head
  g.fillStyle(0xf2ead2, 1);
  g.fillEllipse(5, 6, 7, 5);
  // Brown arrow on head
  g.fillStyle(0x8c5a2e, 1);
  g.fillTriangle(4, 4, 7, 4, 5.5, 7);
  // Horns (small nubs)
  g.fillRect(2, 4, 1, 1);
  g.fillRect(7, 4, 1, 1);
  // Eye
  g.fillStyle(0x000000, 1);
  g.fillRect(5, 6, 1, 1);
  g.generateTexture(key, w, h);
  g.destroy();
}

// ─── 32-tier hero objects (HOUSE-DESIGN-BRIEF.md pixel-art tier) ───────
// Authored at 32-96 px native. Top-down 3/4 perspective shapes with rich
// shading: top-left highlight, base color, bottom-right shadow, 1-px dark
// outline. The detail level matches Eastward / Sea of Stars indie pixel.

// Kitchen island — long counter w/ cream stone top, walnut base, 4 bar
// stools tucked under the south side, amber LED toe-kick glow.
function makeKitchenIslandTexture(scene, key) {
  const w = 96, h = 56;
  const g = scene.add.graphics({ x: 0, y: 0 });

  // Drop shadow
  g.fillStyle(0x000000, 0.25);
  g.fillEllipse(w / 2, h - 2, w - 12, 4);

  // Walnut base (sides + south face)
  const WALNUT = 0x6B4423;
  const WALNUT_DARK = 0x4A2F1C;
  const WALNUT_LIGHT = 0x8B5E3C;
  g.fillStyle(WALNUT_DARK, 1);
  g.fillRect(4, 18, w - 8, 30); // base block
  g.fillStyle(WALNUT, 1);
  g.fillRect(6, 18, w - 12, 26); // front face
  // Cabinet seam lines
  g.fillStyle(WALNUT_DARK, 0.7);
  for (let x = w / 4; x < w; x += w / 4) {
    g.fillRect(Math.floor(x), 20, 1, 22);
  }
  // Highlight on top edge of base
  g.fillStyle(WALNUT_LIGHT, 0.6);
  g.fillRect(6, 18, w - 12, 1);

  // Cream stone counter top (the visible plane from above)
  const STONE = 0xDCD0B8;
  const STONE_LIGHT = 0xEDE4D0;
  const STONE_SHADOW = 0xB8AC92;
  g.fillStyle(STONE_SHADOW, 1);
  g.fillRect(2, 8, w - 4, 12);
  g.fillStyle(STONE, 1);
  g.fillRect(2, 6, w - 4, 11);
  // Top-edge highlight
  g.fillStyle(STONE_LIGHT, 0.8);
  g.fillRect(2, 6, w - 4, 1);
  // Stone veining
  g.fillStyle(STONE_SHADOW, 0.4);
  g.fillRect(20, 9, 16, 1);
  g.fillRect(50, 11, 24, 1);

  // Sink basin (north side, off-center)
  g.fillStyle(0x2A2A2A, 1);
  g.fillRect(60, 9, 12, 6);
  g.fillStyle(0x4A4A4A, 1);
  g.fillRect(61, 10, 10, 4);
  // Faucet
  g.fillStyle(0x9b9b9b, 1);
  g.fillRect(64, 7, 1, 4);
  g.fillRect(65, 7, 4, 1);

  // Bowl on counter (with fruit)
  g.fillStyle(WALNUT, 1);
  g.fillEllipse(28, 13, 10, 4);
  g.fillStyle(0xff8a3a, 1);
  g.fillRect(25, 11, 2, 2);
  g.fillRect(28, 11, 2, 2);

  // Smart speaker (matte black cylinder)
  g.fillStyle(0x14110e, 1);
  g.fillEllipse(86, 13, 6, 4);
  g.fillStyle(0xF6B93B, 0.6);
  g.fillEllipse(86, 12, 5, 1);

  // Bar stools (4 along south side, peeking from under)
  const STOOL = 0xB85C2A; // cognac
  const STOOL_DARK = 0x7a3818;
  const stoolY = 44;
  const stoolXs = [14, 32, 54, 78];
  for (const sx of stoolXs) {
    g.fillStyle(0x000000, 0.3);
    g.fillEllipse(sx, h - 2, 10, 2);
    // Seat cushion (top half visible)
    g.fillStyle(STOOL_DARK, 1);
    g.fillRect(sx - 5, stoolY + 2, 10, 5);
    g.fillStyle(STOOL, 1);
    g.fillRect(sx - 5, stoolY, 10, 5);
    g.fillStyle(0xD27C4A, 0.7);
    g.fillRect(sx - 5, stoolY, 10, 1);
    // Hairpin legs (just thin lines)
    g.fillStyle(0x222222, 1);
    g.fillRect(sx - 4, stoolY + 7, 1, 5);
    g.fillRect(sx + 3, stoolY + 7, 1, 5);
  }

  // Amber LED toe-kick — thin warm glow strip along the very bottom of
  // the base, casting onto floor. The smart-home accent.
  g.fillStyle(0xF6B93B, 0.7);
  g.fillRect(6, h - 4, w - 12, 1);
  g.fillStyle(0xF6B93B, 0.35);
  g.fillRect(4, h - 3, w - 8, 2);

  // Outline
  g.lineStyle(1, 0x14110e, 0.85);
  g.strokeRect(2, 6, w - 4, 11);          // counter
  g.strokeRect(4, 18, w - 8, 30);          // base block

  g.generateTexture(key, w, h);
  g.destroy();
}

// Eames lounge chair + ottoman in cognac leather, walnut frame.
function makeEamesChairTexture(scene, key) {
  const w = 56, h = 44;
  const g = scene.add.graphics({ x: 0, y: 0 });

  // Drop shadow
  g.fillStyle(0x000000, 0.3);
  g.fillEllipse(20, h - 2, 28, 4);
  g.fillStyle(0x000000, 0.25);
  g.fillEllipse(46, h - 2, 16, 3);

  const COGNAC = 0xB85C2A;
  const COGNAC_LIGHT = 0xD2784A;
  const COGNAC_DARK = 0x7a3818;
  const WALNUT = 0x4A2F1C;
  const WALNUT_LIGHT = 0x6B4423;
  const STEEL = 0x6b6b6b;

  // Ottoman (right side, smaller)
  g.fillStyle(COGNAC_DARK, 1);
  g.fillRect(38, 24, 16, 12);
  g.fillStyle(COGNAC, 1);
  g.fillRect(38, 22, 16, 10);
  g.fillStyle(COGNAC_LIGHT, 0.7);
  g.fillRect(38, 22, 16, 2);
  // Tufted seam dots
  g.fillStyle(COGNAC_DARK, 0.6);
  g.fillRect(42, 26, 1, 1);
  g.fillRect(46, 26, 1, 1);
  g.fillRect(50, 26, 1, 1);
  g.fillRect(42, 30, 1, 1);
  g.fillRect(46, 30, 1, 1);
  g.fillRect(50, 30, 1, 1);
  // Ottoman base star (5-prong steel base)
  g.fillStyle(STEEL, 1);
  g.fillRect(40, 36, 12, 2);
  g.fillRect(45, 33, 2, 5);

  // Main lounge chair (left)
  // Backrest (taller, walnut shell visible behind cushion)
  g.fillStyle(WALNUT, 1);
  g.fillRect(4, 4, 30, 22);
  g.fillStyle(WALNUT_LIGHT, 0.7);
  g.fillRect(4, 4, 30, 1);
  // Cognac cushion on backrest
  g.fillStyle(COGNAC_DARK, 1);
  g.fillRect(7, 6, 24, 18);
  g.fillStyle(COGNAC, 1);
  g.fillRect(7, 6, 24, 17);
  g.fillStyle(COGNAC_LIGHT, 0.7);
  g.fillRect(7, 6, 24, 2);
  // Tufted dots on backrest
  g.fillStyle(COGNAC_DARK, 0.6);
  g.fillRect(11, 11, 1, 1);
  g.fillRect(17, 11, 1, 1);
  g.fillRect(23, 11, 1, 1);
  g.fillRect(29, 11, 1, 1);
  g.fillRect(11, 17, 1, 1);
  g.fillRect(17, 17, 1, 1);
  g.fillRect(23, 17, 1, 1);
  g.fillRect(29, 17, 1, 1);

  // Seat cushion (front, lower)
  g.fillStyle(WALNUT, 1);
  g.fillRect(2, 24, 34, 12);
  g.fillStyle(COGNAC_DARK, 1);
  g.fillRect(4, 26, 30, 10);
  g.fillStyle(COGNAC, 1);
  g.fillRect(4, 26, 30, 8);
  g.fillStyle(COGNAC_LIGHT, 0.6);
  g.fillRect(4, 26, 30, 1);

  // Star base (5-prong steel)
  g.fillStyle(STEEL, 1);
  g.fillRect(8, 36, 22, 2);
  g.fillRect(17, 33, 4, 6);

  // Outline
  g.lineStyle(1, 0x14110e, 0.85);
  g.strokeRect(4, 4, 30, 22);
  g.strokeRect(2, 24, 34, 12);
  g.strokeRect(38, 22, 16, 14);

  g.generateTexture(key, w, h);
  g.destroy();
}

// Modernist fireplace — cream stone slab with horizontal opening, soft
// fire glow, wall-mounted TV above.
function makeFireplaceTexture(scene, key) {
  const w = 64, h = 64;
  const g = scene.add.graphics({ x: 0, y: 0 });

  const STONE = 0xDCD0B8;
  const STONE_LIGHT = 0xEDE4D0;
  const STONE_SHADOW = 0xB8AC92;
  const STONE_DEEP = 0x8a7e6b;
  const FIRE_DEEP = 0x6b1f08;
  const FIRE_MID = 0xd44a18;
  const FIRE_HOT = 0xF6B93B;

  // Stone slab body
  g.fillStyle(STONE_SHADOW, 1);
  g.fillRect(0, 0, w, h - 4);
  g.fillStyle(STONE, 1);
  g.fillRect(0, 0, w - 2, h - 6);
  g.fillStyle(STONE_LIGHT, 0.7);
  g.fillRect(0, 0, w - 2, 2);
  g.fillStyle(STONE_LIGHT, 0.4);
  g.fillRect(0, 0, 2, h - 6);
  // Stone veining
  g.fillStyle(STONE_SHADOW, 0.4);
  g.fillRect(8, 18, 16, 1);
  g.fillRect(40, 30, 18, 1);
  g.fillRect(20, 50, 12, 1);

  // Wall-mounted TV (modern flat panel, top half of slab)
  g.fillStyle(0x14110e, 1);
  g.fillRect(8, 6, 48, 18);
  g.fillStyle(0x2a2a2a, 1);
  g.fillRect(10, 8, 44, 14);
  // Subtle screen reflection
  g.fillStyle(0x3a3a3a, 0.6);
  g.fillRect(11, 9, 18, 1);
  g.fillRect(11, 9, 1, 4);
  // Power LED
  g.fillStyle(0xff5050, 1);
  g.fillRect(53, 21, 1, 1);

  // Soundbar below TV
  g.fillStyle(0x14110e, 1);
  g.fillRect(12, 26, 40, 3);
  g.fillStyle(0x2a2a2a, 0.8);
  g.fillRect(13, 27, 38, 1);

  // Horizontal fire opening (lower half of slab)
  g.fillStyle(STONE_DEEP, 1);
  g.fillRect(8, 38, 48, 14);
  g.fillStyle(0x14110e, 1);
  g.fillRect(10, 40, 44, 10);
  // Fire (animated-feeling pixel cluster)
  g.fillStyle(FIRE_DEEP, 1);
  g.fillRect(14, 44, 36, 6);
  g.fillStyle(FIRE_MID, 1);
  g.fillRect(16, 43, 32, 6);
  g.fillStyle(FIRE_HOT, 1);
  g.fillRect(20, 44, 6, 4);
  g.fillRect(30, 43, 6, 5);
  g.fillRect(40, 44, 5, 4);
  // Glow above fire
  g.fillStyle(FIRE_HOT, 0.4);
  g.fillRect(12, 36, 40, 2);
  g.fillStyle(FIRE_MID, 0.3);
  g.fillRect(8, 34, 48, 2);

  // Outline
  g.lineStyle(1, 0x14110e, 0.85);
  g.strokeRect(0, 0, w - 2, h - 6);
  g.strokeRect(8, 6, 48, 18);
  g.strokeRect(8, 38, 48, 14);

  g.generateTexture(key, w, h);
  g.destroy();
}

// Long walnut dining table with chairs around it.
function makeDiningTableTexture(scene, key) {
  const w = 80, h = 56;
  const g = scene.add.graphics({ x: 0, y: 0 });

  // Drop shadow
  g.fillStyle(0x000000, 0.25);
  g.fillEllipse(w / 2, h - 2, w - 8, 4);

  const WALNUT = 0x6B4423;
  const WALNUT_DARK = 0x4A2F1C;
  const WALNUT_LIGHT = 0x8B5E3C;

  // Chairs along north side (4 chairs, backs visible)
  const chairXs = [12, 28, 50, 66];
  for (const cx of chairXs) {
    // Chair back
    g.fillStyle(0x2c2620, 1);
    g.fillRect(cx - 5, 4, 10, 8);
    g.fillStyle(0x4a4138, 1);
    g.fillRect(cx - 5, 4, 10, 1);
    // Seat (peeks above table edge)
    g.fillStyle(WALNUT_DARK, 1);
    g.fillRect(cx - 5, 12, 10, 4);
  }

  // Chairs along south side (4 chairs, peeking out)
  for (const cx of chairXs) {
    // Seat peeks
    g.fillStyle(WALNUT_DARK, 1);
    g.fillRect(cx - 5, h - 14, 10, 4);
    // Chair back (south chair backs angle the other way; small slivers)
    g.fillStyle(0x2c2620, 1);
    g.fillRect(cx - 5, h - 10, 10, 6);
  }

  // Table top (walnut, long rectangle)
  g.fillStyle(WALNUT_DARK, 1);
  g.fillRect(2, 16, w - 4, 24);
  g.fillStyle(WALNUT, 1);
  g.fillRect(2, 14, w - 4, 24);
  g.fillStyle(WALNUT_LIGHT, 0.7);
  g.fillRect(2, 14, w - 4, 1);
  // Wood grain lines
  g.fillStyle(WALNUT_DARK, 0.4);
  for (let x = 8; x < w - 4; x += 12) {
    g.fillRect(x, 18, 1, 18);
  }

  // Centerpiece: small plant in cream pot
  g.fillStyle(0xDCD0B8, 1);
  g.fillRect(w / 2 - 3, 24, 6, 4);
  g.fillStyle(0x67835C, 1);
  g.fillRect(w / 2 - 3, 21, 6, 3);
  g.fillStyle(0x8DB15D, 1);
  g.fillRect(w / 2 - 4, 20, 2, 2);
  g.fillRect(w / 2 + 2, 20, 2, 2);

  // Two place settings (warm pendant glow circles on the table from above)
  g.fillStyle(0xF6E27F, 0.25);
  g.fillCircle(20, 26, 6);
  g.fillCircle(60, 26, 6);

  // Outline
  g.lineStyle(1, 0x14110e, 0.85);
  g.strokeRect(2, 14, w - 4, 24);

  g.generateTexture(key, w, h);
  g.destroy();
}

// L-shaped sectional sofa in cream upholstery.
function makeSectionalSofaTexture(scene, key) {
  const w = 96, h = 64;
  const g = scene.add.graphics({ x: 0, y: 0 });

  // Drop shadow
  g.fillStyle(0x000000, 0.25);
  g.fillEllipse(40, h - 2, 70, 5);

  const CREAM = 0xE8DCC4;
  const CREAM_LIGHT = 0xF3EAD5;
  const CREAM_SHADOW = 0xC4B89A;
  const CUSHION = 0xD2C4A0;
  const ACCENT = 0xB85C2A; // throw pillow

  // Long horizontal section (south side of L)
  g.fillStyle(CREAM_SHADOW, 1);
  g.fillRect(2, 22, 78, 38);
  g.fillStyle(CREAM, 1);
  g.fillRect(2, 22, 76, 36);
  g.fillStyle(CREAM_LIGHT, 0.6);
  g.fillRect(2, 22, 76, 2);

  // Seat cushions (3 on long section)
  for (let i = 0; i < 3; i++) {
    const cx = 6 + i * 24;
    g.fillStyle(CUSHION, 1);
    g.fillRect(cx, 28, 22, 18);
    g.fillStyle(CREAM_LIGHT, 0.5);
    g.fillRect(cx, 28, 22, 1);
    // Cushion seam
    g.fillStyle(CREAM_SHADOW, 0.7);
    g.fillRect(cx + 22, 28, 1, 18);
  }

  // Back cushions (3 along the back)
  for (let i = 0; i < 3; i++) {
    const cx = 6 + i * 24;
    g.fillStyle(CREAM_LIGHT, 1);
    g.fillRect(cx, 24, 22, 6);
  }

  // Short vertical section (east end of L — corner chaise)
  g.fillStyle(CREAM_SHADOW, 1);
  g.fillRect(64, 4, 30, 26);
  g.fillStyle(CREAM, 1);
  g.fillRect(64, 4, 28, 24);
  g.fillStyle(CREAM_LIGHT, 0.6);
  g.fillRect(64, 4, 28, 2);
  // Cushion on chaise
  g.fillStyle(CUSHION, 1);
  g.fillRect(68, 8, 22, 18);
  g.fillStyle(CREAM_LIGHT, 0.5);
  g.fillRect(68, 8, 22, 1);

  // Throw pillow (cognac accent on left end)
  g.fillStyle(ACCENT, 1);
  g.fillRect(8, 30, 10, 8);
  g.fillStyle(0xD27C4A, 0.6);
  g.fillRect(8, 30, 10, 1);
  g.fillStyle(0x7a3818, 0.5);
  g.fillRect(8, 37, 10, 1);

  // Throw blanket draped over far-right end (sage green)
  g.fillStyle(0x67835C, 1);
  g.fillRect(70, 38, 18, 12);
  g.fillStyle(0x8DB15D, 0.5);
  g.fillRect(70, 38, 18, 1);
  // Tassel hint
  g.fillStyle(0x67835C, 1);
  g.fillRect(72, 50, 1, 3);
  g.fillRect(76, 50, 1, 3);
  g.fillRect(82, 50, 1, 3);

  // Outline
  g.lineStyle(1, 0x14110e, 0.85);
  g.strokeRect(2, 22, 76, 36);
  g.strokeRect(64, 4, 28, 24);

  g.generateTexture(key, w, h);
  g.destroy();
}

// Outdoor pool lounger / chaise with cream cushion + walnut frame.
function makeLoungerTexture(scene, key) {
  const w = 32, h = 56;
  const g = scene.add.graphics({ x: 0, y: 0 });

  // Drop shadow
  g.fillStyle(0x000000, 0.3);
  g.fillEllipse(w / 2, h - 2, 22, 3);

  const CUSHION = 0xE8DCC4;
  const CUSHION_LIGHT = 0xF3EAD5;
  const CUSHION_SHADOW = 0xC4B89A;
  const FRAME = 0x4A2F1C;
  const FRAME_LIGHT = 0x6B4423;

  // Walnut frame base
  g.fillStyle(FRAME, 1);
  g.fillRect(4, 8, 24, 44);
  g.fillStyle(FRAME_LIGHT, 0.6);
  g.fillRect(4, 8, 24, 1);
  g.fillStyle(FRAME_LIGHT, 0.4);
  g.fillRect(4, 8, 1, 44);

  // Cushion (longer, with reclined backrest at top)
  g.fillStyle(CUSHION_SHADOW, 1);
  g.fillRect(6, 4, 20, 46);
  g.fillStyle(CUSHION, 1);
  g.fillRect(6, 4, 20, 44);
  g.fillStyle(CUSHION_LIGHT, 0.5);
  g.fillRect(6, 4, 20, 1);

  // Cushion seams (3 sections)
  g.fillStyle(CUSHION_SHADOW, 0.6);
  g.fillRect(6, 18, 20, 1);
  g.fillRect(6, 32, 20, 1);

  // Pillow at the head (top, slightly puffier)
  g.fillStyle(CUSHION_LIGHT, 1);
  g.fillRect(8, 6, 16, 8);
  g.fillStyle(CUSHION, 0.6);
  g.fillRect(8, 12, 16, 2);

  // Outline
  g.lineStyle(1, 0x14110e, 0.85);
  g.strokeRect(4, 4, 24, 48);

  g.generateTexture(key, w, h);
  g.destroy();
}

// Kitchen back-wall (north wall of great room): range + range hood + sink
// + integrated fridge + pantry doorway. Displays as a long horizontal
// strip behind the kitchen island. Authored at 128×40 source.
function makeKitchenBackWallTexture(scene, key) {
  const w = 128, h = 40;
  const g = scene.add.graphics({ x: 0, y: 0 });

  const WALNUT = 0x6B4423;
  const WALNUT_DARK = 0x4A2F1C;
  const WALNUT_LIGHT = 0x8B5E3C;
  const STONE = 0xDCD0B8;
  const STAINLESS = 0xb5b5b5;
  const STAINLESS_LIGHT = 0xd4d4d4;
  const STAINLESS_DARK = 0x7a7a7a;

  // Cream-stone backsplash strip across full width
  g.fillStyle(STONE, 1);
  g.fillRect(0, 0, w, 8);
  g.fillStyle(0xb8ac92, 0.5);
  g.fillRect(0, 6, w, 2);

  // Range + hood (left third)
  // Hood
  g.fillStyle(STAINLESS_DARK, 1);
  g.fillRect(8, 0, 28, 6);
  g.fillStyle(STAINLESS, 1);
  g.fillRect(8, 0, 28, 4);
  g.fillStyle(STAINLESS_LIGHT, 0.7);
  g.fillRect(8, 0, 28, 1);
  // Hood vent line
  g.fillStyle(STAINLESS_DARK, 0.6);
  g.fillRect(10, 3, 24, 1);

  // Range body (below backsplash)
  g.fillStyle(STAINLESS_DARK, 1);
  g.fillRect(8, 8, 28, 32);
  g.fillStyle(STAINLESS, 1);
  g.fillRect(8, 8, 28, 30);
  g.fillStyle(STAINLESS_LIGHT, 0.6);
  g.fillRect(8, 8, 28, 1);
  // Cooktop burners (4 dots, red knobs below)
  g.fillStyle(0x14110e, 1);
  g.fillCircle(14, 14, 2);
  g.fillCircle(22, 14, 2);
  g.fillCircle(30, 14, 2);
  g.fillCircle(14, 20, 2);
  // Oven door
  g.fillStyle(0x1a1a1a, 1);
  g.fillRect(10, 22, 24, 14);
  g.fillStyle(0x3a3a3a, 1);
  g.fillRect(11, 23, 22, 12);
  // Oven handle
  g.fillStyle(STAINLESS_LIGHT, 1);
  g.fillRect(13, 25, 18, 1);
  // Red control knobs
  g.fillStyle(0xc94c4c, 1);
  g.fillCircle(13, 38, 1);
  g.fillCircle(18, 38, 1);
  g.fillCircle(26, 38, 1);
  g.fillCircle(31, 38, 1);

  // Sink + counter (middle third)
  // Counter under
  g.fillStyle(STONE, 1);
  g.fillRect(40, 8, 50, 6);
  g.fillStyle(0xb8ac92, 0.5);
  g.fillRect(40, 13, 50, 1);
  // Counter base cabinets (walnut)
  g.fillStyle(WALNUT_DARK, 1);
  g.fillRect(40, 14, 50, 26);
  g.fillStyle(WALNUT, 1);
  g.fillRect(40, 14, 48, 24);
  g.fillStyle(WALNUT_LIGHT, 0.6);
  g.fillRect(40, 14, 48, 1);
  // Cabinet handles
  g.fillStyle(0xc9a55a, 1);
  g.fillRect(48, 24, 4, 1);
  g.fillRect(64, 24, 4, 1);
  g.fillRect(80, 24, 4, 1);
  // Sink basin
  g.fillStyle(0x2a2a2a, 1);
  g.fillRect(56, 8, 18, 6);
  g.fillStyle(0x4a4a4a, 1);
  g.fillRect(57, 9, 16, 4);
  // Faucet
  g.fillStyle(STAINLESS_LIGHT, 1);
  g.fillRect(64, 4, 1, 5);
  g.fillRect(65, 4, 4, 1);

  // Fridge + pantry doorway (right third)
  // Fridge (panel-front, blends with walnut cabinets)
  g.fillStyle(STAINLESS_DARK, 1);
  g.fillRect(94, 0, 18, 40);
  g.fillStyle(STAINLESS, 1);
  g.fillRect(94, 0, 16, 38);
  g.fillStyle(STAINLESS_LIGHT, 0.6);
  g.fillRect(94, 0, 16, 1);
  // Fridge handles (vertical, on right edge of doors)
  g.fillStyle(STAINLESS_LIGHT, 1);
  g.fillRect(108, 8, 1, 12);
  g.fillRect(108, 24, 1, 12);
  // Door split line
  g.fillStyle(STAINLESS_DARK, 0.6);
  g.fillRect(94, 20, 16, 1);

  // Pantry doorway (right end)
  g.fillStyle(WALNUT_DARK, 1);
  g.fillRect(114, 0, 14, 40);
  g.fillStyle(0x14110e, 1);
  g.fillRect(116, 4, 10, 32);
  // Doorknob
  g.fillStyle(0xc9a55a, 1);
  g.fillRect(124, 20, 1, 1);

  // Outline
  g.lineStyle(1, 0x14110e, 0.7);
  g.strokeRect(0, 0, w - 1, h - 1);

  g.generateTexture(key, w, h);
  g.destroy();
}

// "KUNDIG" framed mountain poster — small wall art for the east end of
// the great-room north wall. Authored at 24×20.
function makeKundigPosterTexture(scene, key) {
  const w = 24, h = 20;
  const g = scene.add.graphics({ x: 0, y: 0 });

  // Frame
  g.fillStyle(0x4A2F1C, 1);
  g.fillRect(0, 0, w, h);
  // Poster background (warm sky)
  g.fillStyle(0xF6B93B, 1);
  g.fillRect(2, 2, w - 4, h - 8);
  // Mountains silhouette
  g.fillStyle(0x4C468B, 1);
  g.fillTriangle(2, 12, 8, 4, 14, 12);
  g.fillTriangle(10, 12, 16, 6, 22, 12);
  // Dark ridge below
  g.fillStyle(0x2c2238, 1);
  g.fillRect(2, 12, w - 4, 2);
  // Snow caps
  g.fillStyle(0xFFFFFF, 1);
  g.fillRect(7, 5, 2, 1);
  g.fillRect(15, 7, 2, 1);
  // Title bar
  g.fillStyle(0xF3E5C2, 1);
  g.fillRect(2, h - 6, w - 4, 4);
  g.fillStyle(0x4A2F1C, 1);
  g.fillRect(6, h - 4, 2, 1);   // K
  g.fillRect(8, h - 4, 1, 1);
  g.fillRect(10, h - 4, 1, 1);  // U
  g.fillRect(12, h - 4, 1, 1);  // N
  g.fillRect(14, h - 4, 1, 1);  // D
  g.fillRect(16, h - 4, 1, 1);  // I
  g.fillRect(18, h - 4, 1, 1);  // G

  g.generateTexture(key, w, h);
  g.destroy();
}

// Cream / sheepskin rug for under the living area. 64×48 source.
function makeRugTexture(scene, key) {
  const w = 64, h = 48;
  const g = scene.add.graphics({ x: 0, y: 0 });

  const RUG = 0xF3EAD5;
  const RUG_SHADOW = 0xDCD0B8;
  const RUG_DARK = 0xB8AC92;

  // Soft organic outline (oval-ish)
  g.fillStyle(RUG_DARK, 0.7);
  g.fillEllipse(w / 2, h / 2, w - 4, h - 6);
  g.fillStyle(RUG_SHADOW, 1);
  g.fillEllipse(w / 2, h / 2, w - 6, h - 8);
  g.fillStyle(RUG, 1);
  g.fillEllipse(w / 2, h / 2 - 1, w - 8, h - 12);
  // Top highlight
  g.fillStyle(0xFFFFFF, 0.3);
  g.fillEllipse(w / 2, h / 2 - 4, w - 14, 4);
  // Tassel hint at edges
  g.fillStyle(RUG_DARK, 0.6);
  for (let x = 6; x < w - 6; x += 4) {
    g.fillRect(x, h - 6, 1, 2);
  }

  g.generateTexture(key, w, h);
  g.destroy();
}

// Tall floor lamp with warm bulb glow. 16×40 source.
function makeFloorLampTexture(scene, key) {
  const w = 16, h = 40;
  const g = scene.add.graphics({ x: 0, y: 0 });

  // Shadow
  g.fillStyle(0x000000, 0.3);
  g.fillEllipse(w / 2, h - 1, 10, 2);

  // Brass base
  g.fillStyle(0xc9a55a, 1);
  g.fillRect(4, h - 4, 8, 3);
  g.fillStyle(0xa8884a, 1);
  g.fillRect(4, h - 2, 8, 1);
  // Pole (thin brass)
  g.fillStyle(0xc9a55a, 1);
  g.fillRect(w / 2, 12, 1, h - 16);
  g.fillStyle(0xa8884a, 1);
  g.fillRect(w / 2 + 1, 12, 1, h - 16);
  // Lampshade (warm)
  g.fillStyle(0xF6B93B, 1);
  g.fillRect(2, 2, 12, 10);
  g.fillStyle(0xF6E27F, 1);
  g.fillRect(3, 3, 10, 9);
  g.fillStyle(0xFFFFFF, 0.4);
  g.fillRect(4, 4, 4, 3);
  // Bulb glow underneath the shade
  g.fillStyle(0xF6B93B, 0.4);
  g.fillEllipse(w / 2, 14, 14, 6);
  g.fillStyle(0xF6E27F, 0.25);
  g.fillEllipse(w / 2, 14, 22, 10);

  g.generateTexture(key, w, h);
  g.destroy();
}

// Snake plant in cream pot. 16×24 source.
function makeSnakePlantTexture(scene, key) {
  const w = 16, h = 24;
  const g = scene.add.graphics({ x: 0, y: 0 });

  // Shadow
  g.fillStyle(0x000000, 0.25);
  g.fillEllipse(w / 2, h - 1, 12, 2);

  // Pot
  g.fillStyle(0xDCD0B8, 1);
  g.fillRect(3, 16, 10, 6);
  g.fillStyle(0xB8AC92, 1);
  g.fillRect(3, 21, 10, 1);
  g.fillStyle(0xF3E5C2, 0.7);
  g.fillRect(3, 16, 10, 1);

  // Snake plant leaves (vertical stripes, several blades)
  // Leaf 1 (left, tall)
  g.fillStyle(0x67835C, 1);
  g.fillRect(4, 0, 1, 16);
  g.fillStyle(0x8DB15D, 1);
  g.fillRect(4, 0, 1, 12);
  g.fillStyle(0xA4C263, 0.8);
  g.fillRect(4, 4, 1, 4);

  // Leaf 2 (left-center)
  g.fillStyle(0x67835C, 1);
  g.fillRect(6, 2, 1, 14);
  g.fillStyle(0x8DB15D, 1);
  g.fillRect(6, 2, 1, 10);

  // Leaf 3 (center, tallest)
  g.fillStyle(0x67835C, 1);
  g.fillRect(8, 0, 1, 16);
  g.fillStyle(0x8DB15D, 1);
  g.fillRect(8, 0, 1, 14);
  g.fillStyle(0xC0D470, 0.8);
  g.fillRect(8, 2, 1, 6);

  // Leaf 4 (right-center)
  g.fillStyle(0x67835C, 1);
  g.fillRect(10, 1, 1, 15);
  g.fillStyle(0x8DB15D, 1);
  g.fillRect(10, 1, 1, 11);

  // Leaf 5 (right, short)
  g.fillStyle(0x67835C, 1);
  g.fillRect(12, 4, 1, 12);
  g.fillStyle(0x8DB15D, 1);
  g.fillRect(12, 4, 1, 9);

  g.generateTexture(key, w, h);
  g.destroy();
}

// ─── v1 Studio sprite generators (placeholder programmatic art) ───────
// All 16-tier chunky-pixel style. Replaced by AI overlay PNGs when those
// land per V1-STUDIO-SCOPE.md "Round 4 shell+overlay" pattern.

// Remi: heart-shaped sketch on cream paper, pinned to wall
function makeStudioRemiSketchTexture(scene, key) {
  const w = 18;
  const h = 18;
  const g = scene.add.graphics({ x: 0, y: 0 });
  // Paper background (slightly off-cream)
  g.fillStyle(0xF3E5C2, 1);
  g.fillRect(0, 0, w, h);
  g.lineStyle(1, 0x8a7050, 1);
  g.strokeRect(0, 0, w, h);
  // Hand-drawn heart, charcoal lines
  g.fillStyle(0xff8fa3, 1);
  // Two top lobes
  g.fillRect(4, 5, 3, 3);
  g.fillRect(11, 5, 3, 3);
  g.fillRect(3, 6, 5, 3);
  g.fillRect(10, 6, 5, 3);
  // V-shape body
  g.fillRect(4, 9, 10, 2);
  g.fillRect(5, 11, 8, 2);
  g.fillRect(7, 13, 4, 2);
  g.fillRect(8, 15, 2, 1);
  // Charcoal outline strokes for hand-drawn feel
  g.fillStyle(0x3a2a1a, 0.4);
  g.fillRect(3, 5, 1, 4);
  g.fillRect(14, 5, 1, 4);
  // Pushpin at top
  g.fillStyle(0xc94a4a, 1);
  g.fillRect(8, 1, 2, 2);
  g.generateTexture(key, w, h);
  g.destroy();
}

// Perfect Bracket: paper bracket print with handwritten picks
function makeStudioBracketPrintTexture(scene, key) {
  const w = 24;
  const h = 18;
  const g = scene.add.graphics({ x: 0, y: 0 });
  // Wood frame (matches Apt Hunter / Remi wall art)
  g.fillStyle(0x6B4423, 1);
  g.fillRect(0, 0, w, h);
  // Paper inside frame
  g.fillStyle(0xF3E5C2, 1);
  g.fillRect(2, 2, w - 4, h - 4);
  // Bracket lines (left side) — shifted +2,+1 to fit inside the frame
  g.fillStyle(0x3a2a1a, 1);
  // Round 1 (4 horizontal lines)
  for (let i = 0; i < 4; i++) {
    g.fillRect(4, 4 + i * 3, 5, 1);
  }
  // Round 1 vertical connectors
  g.fillRect(9, 4, 1, 4);
  g.fillRect(9, 10, 1, 4);
  // Round 2 horizontal
  g.fillRect(10, 6, 3, 1);
  g.fillRect(10, 12, 3, 1);
  // Round 2 connector
  g.fillRect(13, 6, 1, 7);
  // Champion line
  g.fillRect(14, 9, 3, 1);
  // Right side mirror (faded)
  g.fillStyle(0x3a2a1a, 0.5);
  for (let i = 0; i < 4; i++) {
    g.fillRect(15, 4 + i * 3, 5, 1);
  }
  // Highlight pick (orange dot — Ian's pick)
  g.fillStyle(0xffb84a, 1);
  g.fillRect(14, 8, 2, 2);
  g.generateTexture(key, w, h);
  g.destroy();
}

// Open laptop sitting on the worktable, screen tilted toward viewer with
// the Claude crab mascot on it. Top 7 rows = screen (bezel + crab),
// bottom 4 rows = aluminum keyboard base. The whole sprite gets a 1-px
// breathing bob via world.config bob flag (see HouseScene._drawObjects).
function makeWorktableLaptopTexture(scene, key) {
  const w = 16;
  const h = 12;
  const g = scene.add.graphics({ x: 0, y: 0 });

  // ── Screen bezel (dark, almost black frame) ──
  g.fillStyle(0x1a1a1f, 1);
  g.fillRect(0, 0, w, 7);

  // ── Screen interior (deep dark so the orange crab pops) ──
  g.fillStyle(0x0d1117, 1);
  g.fillRect(1, 1, 14, 5);

  // ── Claude crab (with headphones) on screen ──
  // Centered horizontally on the screen (cols 1-14). Crab spans cols 3-12.
  // Blue headphones (band + earcups) wrap around a peach body with two
  // angled eyes. Four chunky leg stubs splay out below the body, slightly
  // wider than the body so they read as distinct legs (not body-extension).
  const crabOrange = 0xcc785c;     // Anthropic peach body
  const headphoneBlue = 0x2c5b9a;  // bold blue headphones
  const eyeBlack = 0x1a1a1f;

  // Headband across the top of the crab (row 1)
  g.fillStyle(headphoneBlue, 1);
  g.fillRect(5, 1, 6, 1);

  // Earcups — left + right, rows 2-4 (2px wide × 3px tall each)
  g.fillRect(3, 2, 2, 3);
  g.fillRect(11, 2, 2, 3);

  // Body between the earcups (rows 2-4)
  g.fillStyle(crabOrange, 1);
  g.fillRect(5, 2, 6, 3);

  // Two angled eyes (row 3) — concerned-squint look
  g.fillStyle(eyeBlack, 1);
  g.fillRect(6, 3, 1, 1);
  g.fillRect(9, 3, 1, 1);

  // Four stub legs at row 5 — splayed outside body width with 1-px gaps
  // so each leg reads as its own stub instead of merging with body bottom.
  g.fillStyle(crabOrange, 1);
  g.fillRect(4, 5, 1, 1);
  g.fillRect(6, 5, 1, 1);
  g.fillRect(9, 5, 1, 1);
  g.fillRect(11, 5, 1, 1);

  // ── Hinge (thin shadow line where screen meets base) ──
  g.fillStyle(0x0a0a0a, 1);
  g.fillRect(0, 7, w, 1);

  // ── Keyboard base (aluminum, sits flat on the desk) ──
  g.fillStyle(0x6a6a72, 1);
  g.fillRect(0, 8, w, 3);
  // Top highlight band (window light catches the front edge of the base)
  g.fillStyle(0x8a8a92, 1);
  g.fillRect(0, 8, w, 1);
  // Key hints — small dark dots across the keyboard row
  g.fillStyle(0x2a2a30, 1);
  for (let kx = 2; kx <= 13; kx += 2) {
    g.fillRect(kx, 9, 1, 1);
  }
  // Trackpad outline (small recessed rectangle front-center)
  g.fillStyle(0x52525a, 1);
  g.fillRect(6, 10, 4, 1);

  // ── Ground shadow (where the base meets the desk) ──
  g.fillStyle(0x000000, 0.35);
  g.fillRect(1, 11, 14, 1);

  g.generateTexture(key, w, h);
  g.destroy();
}

// Apartment Hunter: small framed Chicago map with pushpins.
// Lake Michigan sits on the EAST (right) side of the city, so the water
// rendering lives in the right portion of the map and the land/grid is left.
function makeStudioAptHunterMapTexture(scene, key) {
  const w = 20;
  const h = 18;
  const g = scene.add.graphics({ x: 0, y: 0 });
  // Wood frame
  g.fillStyle(0x6B4423, 1);
  g.fillRect(0, 0, w, h);
  // Map paper
  g.fillStyle(0xE8DCC4, 1);
  g.fillRect(2, 2, w - 4, h - 4);
  // Water (Lake Michigan — right side, blue-teal)
  g.fillStyle(0x7ad7d4, 0.6);
  g.fillRect(13, 2, 5, h - 4);
  g.fillStyle(0x7ad7d4, 0.8);
  g.fillRect(14, 4, 4, 6);
  // Land (rest of map — left side)
  g.fillStyle(0xa9b08a, 0.5);
  g.fillRect(2, 2, w - 9, h - 4);
  // Streets (faint grid over land)
  g.fillStyle(0x8a7050, 0.4);
  for (const x of [5, 8, 11]) {
    g.fillRect(x, 3, 1, h - 6);
  }
  for (let y = 4; y < h - 3; y += 3) {
    g.fillRect(3, y, w - 10, 1);
  }
  // Pushpins (3 — red, orange, yellow) on the land side
  g.fillStyle(0xc94a4a, 1);
  g.fillRect(8, 6, 2, 2);
  g.fillStyle(0xffb84a, 1);
  g.fillRect(5, 9, 2, 2);
  g.fillStyle(0xf6e27f, 1);
  g.fillRect(7, 12, 2, 2);
  g.generateTexture(key, w, h);
  g.destroy();
}

// DOFH: small championship trophy (cup on plinth)
function makeStudioDofhTrophyTexture(scene, key) {
  const w = 14;
  const h = 18;
  const g = scene.add.graphics({ x: 0, y: 0 });
  // Plinth (dark wood)
  g.fillStyle(0x4A2F1C, 1);
  g.fillRect(2, 14, 10, 4);
  g.fillStyle(0x6B4423, 1);
  g.fillRect(3, 13, 8, 1);
  // Stem
  g.fillStyle(0xC9A55A, 1);
  g.fillRect(6, 9, 2, 4);
  // Cup body
  g.fillStyle(0xC9A55A, 1);
  g.fillRect(3, 4, 8, 6);
  g.fillStyle(0xE6C77A, 1); // gold highlight
  g.fillRect(3, 4, 8, 1);
  g.fillRect(3, 5, 1, 4);
  // Cup handles
  g.fillStyle(0xC9A55A, 1);
  g.fillRect(1, 5, 2, 4);
  g.fillRect(11, 5, 2, 4);
  // Cup top opening
  g.fillStyle(0x4A2F1C, 1);
  g.fillRect(4, 4, 6, 1);
  // "1" inscription (faint)
  g.fillStyle(0x4A2F1C, 0.5);
  g.fillRect(7, 7, 1, 2);
  g.generateTexture(key, w, h);
  g.destroy();
}

// Worktable: low desk with simple legs (Ian's hangout). 30×14 native →
// scale 2× → 60×28 display ≈ 1.9 × 0.9 tiles. Uses a DARKER walnut tone
// (0x4A2F1C base) than the v1 0x6B4423 so it reads against the AI shell's
// wood floor (which is also walnut-toned). Desk visibly sits on top of
// the floor instead of merging with it.
function makeStudioWorktableTexture(scene, key) {
  const w = 30;
  const h = 14;
  const g = scene.add.graphics({ x: 0, y: 0 });
  // Drop shadow under the desk — wider/darker to anchor it visually
  g.fillStyle(0x000000, 0.32);
  g.fillRect(1, 12, w - 2, 2);
  g.fillStyle(0x000000, 0.18);
  g.fillRect(0, 13, w, 1);
  // Desktop slab — dark walnut for floor contrast
  g.fillStyle(0x4A2F1C, 1);
  g.fillRect(0, 3, w, 5);
  g.fillStyle(0x6B4423, 1); // top highlight (lighter walnut)
  g.fillRect(0, 3, w, 1);
  g.fillStyle(0x2a1a14, 1); // front edge deep shadow
  g.fillRect(0, 7, w, 1);
  // Legs (front-left, front-right) — even darker
  g.fillStyle(0x2a1a14, 1);
  g.fillRect(2, 8, 2, 5);
  g.fillRect(w - 4, 8, 2, 5);
  // Center drawer
  g.fillStyle(0x2a1a14, 0.85);
  g.fillRect(10, 5, 10, 1);
  g.fillStyle(0xC9A55A, 1); // brass pull
  g.fillRect(14, 6, 2, 1);
  // Coffee mug (right side of desk) — cream against dark desk = high contrast
  g.fillStyle(0xF3E5C2, 1);
  g.fillRect(22, 0, 4, 4);
  g.fillStyle(0xc9a55a, 0.6); // mug rim/handle hint
  g.fillRect(26, 1, 1, 2);
  g.fillStyle(0x6B4423, 1); // dark coffee inside
  g.fillRect(23, 1, 2, 1);
  // Open notebook (left side of desk) — same cream contrast
  g.fillStyle(0xF3E5C2, 1);
  g.fillRect(3, 0, 6, 3);
  g.fillStyle(0x3a2a1a, 0.5);
  g.fillRect(3, 1, 6, 1);
  g.generateTexture(key, w, h);
  g.destroy();
}

// TV mounted on wall: dark screen + thin bezel
function makeStudioTvTexture(scene, key) {
  const w = 28;
  const h = 18;
  const g = scene.add.graphics({ x: 0, y: 0 });
  // Wall mount (small bracket behind)
  g.fillStyle(0x2a2018, 1);
  g.fillRect(12, 14, 4, 2);
  // Bezel
  g.fillStyle(0x14110e, 1);
  g.fillRect(0, 0, w, 14);
  // Screen (dark — TV is "off")
  g.fillStyle(0x0a0808, 1);
  g.fillRect(2, 2, w - 4, 10);
  // Subtle screen reflection (top-left highlight)
  g.fillStyle(0x4C468B, 0.18);
  g.fillRect(2, 2, w - 4, 2);
  g.fillStyle(0x4C468B, 0.10);
  g.fillRect(2, 2, 4, 10);
  // Tiny "ON" indicator dot (red, dim)
  g.fillStyle(0xc94a4a, 0.5);
  g.fillRect(w - 4, 11, 1, 1);
  g.generateTexture(key, w, h);
  g.destroy();
}

// Arcade cabinet: tall standing arcade with screen + control panel. 18×30
// so engine scales 2× → 36×60 display ≈ 1.1 × 1.9 tiles.
function makeStudioArcadeCabinetTexture(scene, key) {
  const w = 18;
  const h = 30;
  const g = scene.add.graphics({ x: 0, y: 0 });
  // Body — Caffeine Rush purple
  g.fillStyle(0x4C468B, 1);
  g.fillRect(0, 1, w, h - 2);
  g.fillStyle(0x7F77DD, 1); // top highlight
  g.fillRect(0, 1, w, 1);
  g.fillStyle(0x2A2456, 1); // bottom shadow strip
  g.fillRect(0, h - 2, w, 2);
  // Marquee — orange "CAFFEINE RUSH" hint
  g.fillStyle(0x14110e, 1);
  g.fillRect(2, 3, w - 4, 4);
  g.fillStyle(0xD85A30, 1);
  g.fillRect(3, 4, 2, 2);
  g.fillRect(6, 4, 2, 2);
  g.fillRect(9, 4, 2, 2);
  g.fillRect(12, 4, 2, 2);
  // Screen (game in motion)
  g.fillStyle(0x14110e, 1);
  g.fillRect(2, 8, w - 4, 9);
  g.fillStyle(0x7F77DD, 0.4);
  g.fillRect(3, 9, w - 6, 2);
  g.fillStyle(0xD85A30, 1);
  g.fillRect(7, 12, 4, 2); // coffee bean / rush element
  g.fillStyle(0xC9A55A, 0.7);
  g.fillRect(10, 14, 2, 1); // pixel highlight
  // Control panel
  g.fillStyle(0x2A2456, 1);
  g.fillRect(2, 18, w - 4, 5);
  g.fillStyle(0xD85A30, 1); // joystick
  g.fillRect(5, 19, 2, 2);
  g.fillStyle(0xC9A55A, 1); // buttons
  g.fillRect(10, 19, 1, 1);
  g.fillRect(12, 19, 1, 1);
  g.fillRect(11, 21, 1, 1);
  // Coin slot
  g.fillStyle(0x14110e, 1);
  g.fillRect(7, 24, 4, 1);
  // Front-bottom kick plate (darker)
  g.fillStyle(0x14110e, 0.8);
  g.fillRect(2, 26, w - 4, 2);
  g.generateTexture(key, w, h);
  g.destroy();
}

// Bookshelf: tall walnut bookshelf with rows of books. 18×30 so engine
// scales 2× → 36×60 display ≈ 1.1 × 1.9 tiles. Beefier than the sliced
// Sprout Lands fallback so it reads as a bookshelf, not a small chair.
function makeStudioBookshelfTexture(scene, key) {
  const w = 18;
  const h = 30;
  const g = scene.add.graphics({ x: 0, y: 0 });
  // Frame (dark walnut)
  g.fillStyle(0x4A2F1C, 1);
  g.fillRect(0, 0, w, h);
  // Inner cavity
  g.fillStyle(0x3a2317, 1);
  g.fillRect(2, 2, w - 4, h - 4);
  // Top edge highlight
  g.fillStyle(0x6B4423, 1);
  g.fillRect(0, 0, w, 1);
  // Three shelves with books
  const shelfYs = [5, 12, 19];
  for (const sy of shelfYs) {
    // Shelf board
    g.fillStyle(0x6B4423, 1);
    g.fillRect(2, sy + 5, w - 4, 1);
    // Books — colorful spines, varied heights
    const colors = [0xc94a4a, 0xC9A55A, 0x4C468B, 0x67835C, 0xff8fa3, 0x7ad7d4];
    let bx = 3;
    for (let i = 0; i < 6 && bx < w - 4; i++) {
      const bw = 2; // book width
      const bh = 5 - (i % 3 === 0 ? 1 : 0); // slight height variation
      const c = colors[(i + sy) % colors.length];
      g.fillStyle(c, 1);
      g.fillRect(bx, sy + 5 - bh, bw, bh);
      // Title bands (lighter horizontal stripe)
      g.fillStyle(0xF3E5C2, 0.4);
      g.fillRect(bx, sy + 5 - 2, bw, 1);
      bx += bw + 1;
    }
  }
  // Bottom shelf — closed cabinet with two small handles
  g.fillStyle(0x4A2F1C, 1);
  g.fillRect(2, 25, w - 4, 3);
  g.fillStyle(0xC9A55A, 1);
  g.fillRect(6, 26, 1, 1);
  g.fillRect(11, 26, 1, 1);
  g.generateTexture(key, w, h);
  g.destroy();
}

// Studio mailbox — substantial post-mounted mailbox. 16×30 native so engine
// scales 2× → 32×60 display ≈ 1 × 1.9 tiles. Reads from the SE corner.
function makeStudioMailboxTexture(scene, key) {
  const w = 16;
  const h = 30;
  const g = scene.add.graphics({ x: 0, y: 0 });
  // Ground shadow
  g.fillStyle(0x000000, 0.28);
  g.fillEllipse(w / 2, h - 1, 12, 3);
  // Post (warm walnut)
  g.fillStyle(0x6B4423, 1);
  g.fillRect(7, 14, 2, 14);
  g.fillStyle(0x8a5a30, 1); // post highlight
  g.fillRect(7, 14, 1, 14);
  // Mailbox body — cream stone for that "modern home" vibe matching the brief
  g.fillStyle(0xDCD0B8, 1);
  g.fillRect(2, 3, w - 4, 11);
  // Top highlight
  g.fillStyle(0xEDE4D0, 1);
  g.fillRect(2, 3, w - 4, 1);
  // Bottom shadow
  g.fillStyle(0xB8AC92, 1);
  g.fillRect(2, 13, w - 4, 1);
  // Black mail slot (door)
  g.fillStyle(0x14110e, 1);
  g.fillRect(4, 6, w - 8, 5);
  // Brass knob
  g.fillStyle(0xC9A55A, 1);
  g.fillRect(w - 5, 9, 1, 1);
  // Tiny address number
  g.fillStyle(0x4A2F1C, 1);
  g.fillRect(4, 4, 1, 1);
  g.fillRect(6, 4, 1, 1);
  // Red flag (raised — there's mail!)
  g.fillStyle(0xc94a4a, 1);
  g.fillRect(13, 4, 2, 4);
  g.fillStyle(0x6b4f3a, 1);
  g.fillRect(12, 4, 1, 5);
  // A tiny envelope sticking out the top
  g.fillStyle(0xF3E5C2, 1);
  g.fillRect(5, 2, 5, 2);
  g.fillStyle(0x8a7050, 0.8);
  g.fillRect(5, 2, 5, 1);
  g.generateTexture(key, w, h);
  g.destroy();
}

// Yoga mat — laid out flat on the floor, ready to use. Black mat (matches
// Ian's actual one). Top-down view: long horizontal rectangle, ~3 tiles
// wide × ~1 tile tall.
function makeStudioYogaMatTexture(scene, key) {
  const w = 48;
  const h = 18;
  const g = scene.add.graphics({ x: 0, y: 0 });
  // Soft ground shadow under the mat (mat hovers very slightly to read as flat)
  g.fillStyle(0x000000, 0.18);
  g.fillRect(2, h - 3, w - 4, 2);
  // Mat body — black rubber with a subtle warm undertone
  g.fillStyle(0x1a1a1a, 1);
  g.fillRect(0, 2, w, h - 4);
  // Top edge highlight (slightly lifted lip)
  g.fillStyle(0x303030, 1);
  g.fillRect(0, 2, w, 1);
  // Bottom edge shadow
  g.fillStyle(0x080808, 1);
  g.fillRect(0, h - 3, w, 1);
  // Subtle texture lines along the length (rubber grain)
  g.fillStyle(0x252525, 0.45);
  g.fillRect(2, 6, w - 4, 1);
  g.fillRect(2, 10, w - 4, 1);
  // End caps slightly rounded (cut corners visually)
  g.fillStyle(0x0a0a0a, 1);
  g.fillRect(0, 2, 1, 1);
  g.fillRect(0, h - 4, 1, 1);
  g.fillRect(w - 1, 2, 1, 1);
  g.fillRect(w - 1, h - 4, 1, 1);
  g.generateTexture(key, w, h);
  g.destroy();
}

// Ball basket — woven basket with a soccer ball and a Wilson AVP-style
// yellow/black beach volleyball nestled inside. Single combined sprite so
// the two balls read as "a thing by the door" not two random items.
function makeStudioBallBasketTexture(scene, key) {
  const w = 28;
  const h = 22;
  const g = scene.add.graphics({ x: 0, y: 0 });
  // Ground shadow
  g.fillStyle(0x000000, 0.28);
  g.fillEllipse(w / 2, h - 1, w - 6, 3);

  // Basket body (woven tan)
  g.fillStyle(0xa8784a, 1);
  g.fillRect(2, 10, w - 4, 10);
  // Basket darker shadow side
  g.fillStyle(0x7a5430, 1);
  g.fillRect(2, 18, w - 4, 2);
  // Basket rim (slightly lighter band on top)
  g.fillStyle(0xc8966b, 1);
  g.fillRect(2, 10, w - 4, 2);
  // Weave pattern — vertical lines suggesting wicker
  g.fillStyle(0x7a5430, 0.6);
  for (let x = 4; x < w - 3; x += 3) {
    g.fillRect(x, 12, 1, 6);
  }
  // Horizontal weave line
  g.fillStyle(0x7a5430, 0.5);
  g.fillRect(2, 15, w - 4, 1);

  // Soccer ball — left side, peeks above basket rim
  const sx = 8;
  const sy = 8;
  g.fillStyle(0xf4f4ee, 1);
  g.fillCircle(sx, sy, 5);
  // Soccer ball pattern — 5 black pentagons in a quincunx (4 corners + center).
  // Mirrors the actual soccer-ball panel grid more closely than a 3-spot
  // triangle (which reads as a face).
  g.fillStyle(0x14110e, 1);
  g.fillRect(sx - 3, sy - 3, 2, 2); // top-left
  g.fillRect(sx + 1, sy - 3, 2, 2); // top-right
  g.fillRect(sx - 1, sy - 1, 2, 2); // center
  g.fillRect(sx - 3, sy + 1, 2, 2); // bottom-left
  g.fillRect(sx + 1, sy + 1, 2, 2); // bottom-right
  // Mid-tone grey curve hints on the ball's outer limbs — softens the
  // silhouette so it reads as a sphere instead of a flat white disk.
  g.fillStyle(0xb8b3a8, 1);
  g.fillRect(sx - 4, sy, 1, 1);     // far left limb
  g.fillRect(sx + 4, sy, 1, 1);     // far right limb
  // Single-pixel highlight on the upper-left limb
  g.fillStyle(0xffffff, 1);
  g.fillRect(sx - 3, sy - 1, 1, 1);

  // Beach volleyball — right side, Wilson AVP yellow + black/orange
  const vx = 19;
  const vy = 8;
  // Yellow body
  g.fillStyle(0xf6c945, 1);
  g.fillCircle(vx, vy, 5);
  // Bright yellow highlight
  g.fillStyle(0xfde072, 1);
  g.fillRect(vx - 2, vy - 3, 2, 1);
  // Black/dark panel pattern (suggests the geometric AVP design)
  g.fillStyle(0x14110e, 1);
  g.fillRect(vx, vy - 2, 2, 1);
  g.fillRect(vx + 2, vy, 1, 2);
  g.fillRect(vx - 3, vy + 1, 2, 1);
  g.fillRect(vx - 1, vy + 2, 2, 1);
  // Orange accent (the AVP red/orange flash)
  g.fillStyle(0xe85a2a, 1);
  g.fillRect(vx + 1, vy + 1, 1, 1);
  g.fillRect(vx - 2, vy + 3, 1, 1);

  g.generateTexture(key, w, h);
  g.destroy();
}

// Currently board: cork board with sticky notes (one per "field"). 24×16.
function makeStudioCurrentlyBoardTexture(scene, key) {
  const w = 24;
  const h = 16;
  const g = scene.add.graphics({ x: 0, y: 0 });
  // Wood frame
  g.fillStyle(0x6B4423, 1);
  g.fillRect(0, 0, w, h);
  g.fillStyle(0x8a5a30, 1);
  g.fillRect(0, 0, w, 1);
  // Cork inner
  g.fillStyle(0xc9a55a, 1);
  g.fillRect(2, 2, w - 4, h - 4);
  // Cork texture noise
  g.fillStyle(0xa68543, 0.5);
  for (let i = 0; i < 14; i++) {
    const cx = 3 + ((i * 7) % (w - 6));
    const cy = 3 + ((i * 5) % (h - 6));
    g.fillRect(cx, cy, 1, 1);
  }
  // Sticky notes — 4 across, varied colors
  // Yellow (reading)
  g.fillStyle(0xf6e27f, 1);
  g.fillRect(3, 3, 4, 4);
  g.fillStyle(0xc94a4a, 1); // pushpin
  g.fillRect(4, 3, 1, 1);
  // Pink (watching)
  g.fillStyle(0xff8fa3, 1);
  g.fillRect(8, 4, 4, 4);
  g.fillStyle(0xc94a4a, 1);
  g.fillRect(9, 4, 1, 1);
  // Teal (building)
  g.fillStyle(0x7ad7d4, 1);
  g.fillRect(13, 3, 4, 4);
  g.fillStyle(0xc94a4a, 1);
  g.fillRect(14, 3, 1, 1);
  // Cream (excited about)
  g.fillStyle(0xF3E5C2, 1);
  g.fillRect(18, 5, 4, 4);
  g.fillStyle(0xc94a4a, 1);
  g.fillRect(19, 5, 1, 1);
  // Orange small note bottom
  g.fillStyle(0xffb84a, 1);
  g.fillRect(5, 9, 4, 4);
  // Faint label-line scribbles
  g.fillStyle(0x3a2a1a, 0.5);
  g.fillRect(4, 5, 2, 1);
  g.fillRect(9, 6, 2, 1);
  g.fillRect(14, 5, 2, 1);
  g.generateTexture(key, w, h);
  g.destroy();
}

// Neighbor photo: framed photo with two abstract figures
function makeStudioNeighborPhotoTexture(scene, key) {
  const w = 20;
  const h = 16;
  const g = scene.add.graphics({ x: 0, y: 0 });
  // Wood frame
  g.fillStyle(0x6B4423, 1);
  g.fillRect(0, 0, w, h);
  g.fillStyle(0x8a5a30, 1);
  g.fillRect(0, 0, w, 1);
  g.fillStyle(0x4A2F1C, 1);
  g.fillRect(0, h - 1, w, 1);
  // Photo paper / matte
  g.fillStyle(0xF3E5C2, 1);
  g.fillRect(2, 2, w - 4, h - 4);
  // Sky / background
  g.fillStyle(0xa9b8c4, 1);
  g.fillRect(3, 3, w - 6, 6);
  // Ground
  g.fillStyle(0x8DB15D, 1);
  g.fillRect(3, 8, w - 6, h - 11);
  // Figure 1 (Layne — pinkish)
  g.fillStyle(0xff8fa3, 1);
  g.fillRect(6, 6, 2, 5);
  // Head
  g.fillStyle(0xe7c39c, 1);
  g.fillRect(6, 4, 2, 2);
  // Figure 2 (Elise — teal)
  g.fillStyle(0x7ad7d4, 1);
  g.fillRect(11, 6, 2, 5);
  // Head
  g.fillStyle(0xe7c39c, 1);
  g.fillRect(11, 4, 2, 2);
  // Tiny "?" coming-soon hint at bottom corner
  g.fillStyle(0xc9a55a, 0.6);
  g.fillRect(w - 4, h - 4, 1, 1);
  g.fillRect(w - 3, h - 5, 1, 1);
  g.fillRect(w - 4, h - 6, 1, 1);
  g.generateTexture(key, w, h);
  g.destroy();
}

// ─── Phone-app social icons (32x32, white glyph on transparent — tile
// background colour comes from APPS.accent). Each glyph is a pared-down
// pixel rendering of the platform's logomark, optimised to read at 48px.

function makeLinkedinIcon(scene, key) {
  const size = 32;
  const g = scene.add.graphics({ x: 0, y: 0 });
  // White "in" wordmark, centered. "i" left, "n" right.
  g.fillStyle(0xffffff, 1);
  // "i" dot
  g.fillRect(8, 9, 4, 4);
  // "i" stem
  g.fillRect(8, 14, 4, 11);
  // "n" left vertical
  g.fillRect(15, 14, 4, 11);
  // "n" arch (curves up from left bar to right bar at the top)
  g.fillRect(15, 14, 9, 3);
  g.fillRect(20, 14, 4, 11);
  g.generateTexture(key, size, size);
  g.destroy();
}

function makeGithubIcon(scene, key) {
  const size = 32;
  const g = scene.add.graphics({ x: 0, y: 0 });

  // White circular disc — the GitHub logomark base
  g.fillStyle(0xffffff, 1);
  g.fillCircle(16, 16, 13);

  // Black Octocat silhouette punched into the disc (drawn in accent dark)
  g.fillStyle(0x181717, 1);

  // Two pointed ears at the top
  g.fillRect(10, 5, 2, 1);   // left ear tip
  g.fillRect(9, 6, 3, 1);    // left ear widening
  g.fillRect(9, 7, 4, 1);    // left ear base
  g.fillRect(20, 5, 2, 1);   // right ear tip
  g.fillRect(20, 6, 3, 1);
  g.fillRect(19, 7, 4, 1);

  // Main head body (rounded rectangle filling the disc)
  g.fillRect(8, 8, 16, 11);
  // Soften the head's top corners back to white
  g.fillStyle(0xffffff, 1);
  g.fillRect(8, 8, 1, 1);
  g.fillRect(23, 8, 1, 1);

  // Mouth notch — small white cutout at bottom-center of head
  g.fillRect(14, 19, 4, 1);

  // Two feet at the bottom (octocat sitting)
  g.fillStyle(0x181717, 1);
  g.fillRect(10, 19, 4, 4);   // left foot
  g.fillRect(18, 19, 4, 4);   // right foot

  // Tail — curling out from the lower-left of the body
  g.fillRect(7, 17, 1, 2);
  g.fillRect(6, 19, 1, 2);
  g.fillRect(5, 21, 2, 1);
  g.fillRect(5, 22, 2, 1);

  g.generateTexture(key, size, size);
  g.destroy();
}

function makeInstagramIcon(scene, key) {
  const size = 32;
  const g = scene.add.graphics({ x: 0, y: 0 });
  g.fillStyle(0xffffff, 1);
  // Camera body — square outline drawn as 4 edges (2px thick each)
  // Top edge
  g.fillRect(7, 7, 18, 2);
  // Bottom edge
  g.fillRect(7, 23, 18, 2);
  // Left edge
  g.fillRect(7, 7, 2, 18);
  // Right edge
  g.fillRect(23, 7, 2, 18);
  // Rounded corner softens — clip each corner with the accent color (no,
  // skip; the existing border-radius on the tile wrap already softens the
  // edges visually).
  // Lens — small filled ring (outer circle + inner cutout)
  g.fillCircle(16, 16, 5);
  g.fillStyle(0xE1306C, 1); // cut back to accent pink for the lens "hole"
  g.fillCircle(16, 16, 3);
  // Flash dot (top-right)
  g.fillStyle(0xffffff, 1);
  g.fillRect(20, 10, 2, 2);
  g.generateTexture(key, size, size);
  g.destroy();
}

function makeSpotifyIcon(scene, key) {
  const size = 32;
  const g = scene.add.graphics({ x: 0, y: 0 });
  // Filled white circle with 3 black "sound wave" arcs across it.
  // Spotify's signature: green tile (handled by accent) with white disc
  // and curved cuts.
  g.fillStyle(0xffffff, 1);
  g.fillCircle(16, 16, 11);
  // Three sound waves — drawn as pixel lines bowing right
  g.fillStyle(0x1DB954, 1);
  // Top wave (widest)
  g.fillRect(9, 11, 12, 1);
  g.fillRect(8, 12, 2, 1);
  g.fillRect(20, 12, 2, 1);
  // Middle wave
  g.fillRect(10, 15, 10, 1);
  g.fillRect(9, 16, 2, 1);
  g.fillRect(19, 16, 2, 1);
  // Bottom wave (narrowest)
  g.fillRect(11, 19, 8, 1);
  g.fillRect(10, 20, 2, 1);
  g.fillRect(18, 20, 2, 1);
  g.generateTexture(key, size, size);
  g.destroy();
}
